/**
 * scorecards.service.ts
 *
 * Admin CRUD for conversation scorecard templates (Admin → Account → Scorecards).
 * Backed by /api/v5/scorecards (ScorecardController). Thin apiClient wrappers,
 * same shape as tasks.service.ts.
 */
import { apiClient } from './apiClient';
import type { ApiResponse } from '@/types/api.types';

export interface ScorecardField {
  scorecardFieldID?: number;
  name: string;
  minCriteria: string;
  midCriteria: string;
  maxCriteria: string;
  weight: number;
  sortOrder?: number;
}

export interface ScorecardTemplate {
  scorecardTemplateID: number;
  name: string;
  type: string;
  teamScope: string;
  scoringPrompt: string;
  formattingPrompt: string;
  summaryTakeawaysRules: string;
  ratingMin: number;
  ratingMid: number | null;
  ratingMax: number;
  active: boolean;
  sortOrder: number;
  fields: ScorecardField[];
  fieldCount: number;
}

/** List-row shape (no fields array). */
export type ScorecardTemplateListItem = Omit<ScorecardTemplate, 'fields'>;

export interface ScorecardTemplatePayload {
  name: string;
  scoringPrompt?: string;
  formattingPrompt?: string;
  summaryTakeawaysRules?: string;
  ratingMin?: number;
  ratingMid?: number | null;
  ratingMax?: number;
  active?: boolean;
  sortOrder?: number;
  fields: Array<{ name: string; minCriteria: string; midCriteria: string; maxCriteria: string; weight: number }>;
}

export function listScorecards(): Promise<ScorecardTemplateListItem[]> {
  return apiClient
    .get<ApiResponse<ScorecardTemplateListItem[]>>('/scorecards')
    .then((r) => r.data.data ?? []);
}

export function getScorecard(id: number): Promise<ScorecardTemplate> {
  return apiClient
    .get<ApiResponse<ScorecardTemplate>>(`/scorecards/${id}`)
    .then((r) => r.data.data);
}

export function createScorecard(payload: ScorecardTemplatePayload): Promise<ScorecardTemplate> {
  return apiClient
    .post<ApiResponse<ScorecardTemplate>>('/scorecards', payload)
    .then((r) => r.data.data);
}

export function updateScorecard(
  id: number,
  payload: ScorecardTemplatePayload,
): Promise<ScorecardTemplate> {
  return apiClient
    .put<ApiResponse<ScorecardTemplate>>(`/scorecards/${id}`, payload)
    .then((r) => r.data.data);
}

export function deleteScorecard(id: number): Promise<void> {
  return apiClient.delete(`/scorecards/${id}`).then(() => undefined);
}

export function toggleScorecardActive(id: number, active: boolean): Promise<void> {
  return apiClient.patch(`/scorecards/${id}/active`, { active }).then(() => undefined);
}
